import { BaseService } from "./BaseService";
import config from "../../config";
import { CognitoUser } from "amazon-cognito-identity-js";
import { nanoid } from "nanoid/non-secure";
import { VantivCardTypes } from "../../types/vantiv";
import { PaymentType, AppReviewState } from "../enums";
import { getExpDate } from "../utils/calendar";
import * as FileSystem from "expo-file-system";
import { uuidv4 } from "../utils/uuidv4";
import Constants from "expo-constants";
import { Platform } from "react-native";

import {
  ScanCheckRequestType,
  FindDuplicatesRequestType,
  ScanFrontCheckResponseType,
  ScanBackCheckResponseType,
  FindDuplicatesResponseType,
  SubmitCheckRequestType,
  SubmitCheckResponseType,
} from "../redux/store/check/type";

export interface CreatePaymentBody extends Record<string, any> {
  reservationId: string;
}

interface AddPaymentMethodBody extends Record<string, any> {
  reservationId: string;
  zip: string;
  type: keyof typeof VantivCardTypes;
  cardholder: string;
  expDate: string;
  paypageRegistrationId: string;
  cardNumber: number;
  allowCof: boolean;
  paymentSourceType: string;
  paymentScheduleType: string;
  paymentType: keyof typeof PaymentType;
  streetAddress?: string;
  state?: string;
  streetAddress2?: string;
  city?: string;
  bankAccountNumber?: string;
  paymentRoutingNumber?: string;
  threatMetrixSessionId?: string;
}

interface SubmitAppReviewDataBody extends Record<string, any> {
  state?: AppReviewState;
}

export class DomusoApiService extends BaseService {
  constructor(
    public cognitoUser: CognitoUser,
    public dispatch = (any: any) => {},
    public url = config.domusoApiService
  ) {
    super(url, cognitoUser, dispatch);
  }

  async scanFrontCheck(uri: string): Promise<ScanFrontCheckResponseType> {
    const url = "v1/check_scan/scan/front";
    let fileName = config.mockScanData === "true" ? "front.jpg" : uri.split("/").pop() || "";
    let fileBody = "";

    if (Platform.OS == "web") {
      fileName = config.mockScanData === "true" ? "front.jpg" : `${uuidv4()}.jpg`;
      fileBody = uri.split("base64,")[1];
    } else {
      fileBody = await FileSystem.readAsStringAsync(uri, { encoding: "base64" });
    }

    const data: ScanCheckRequestType = {
      request_id: uuidv4(),
      file_name: fileName,
      file_body: fileBody,
      type: "image/jpeg",
      temp_id: undefined,
    };

    return this.makePostRequest(url, data, true);
  }

  async scanBackCheck({
    uri,
    tempId,
    accountNumber,
    routingNumber,
    propertyName,
  }: Record<string, string>): Promise<ScanBackCheckResponseType> {
    const url = `v1/check_scan/scan/back`;
    let fileName = config.mockScanData === "true" ? "back.jpg" : uri.split("/").pop() || "";
    let fileBody = "";

    if (Platform.OS == "web") {
      fileName = config.mockScanData === "true" ? "front.jpg" : `${uuidv4()}.jpg`;
      fileBody = uri.split("base64,")[1];
    } else {
      fileBody = await FileSystem.readAsStringAsync(uri, { encoding: "base64" });
    }
    const data: ScanCheckRequestType = {
      request_id: uuidv4(),
      file_name: fileName,
      file_body: fileBody,
      account_number: accountNumber,
      routing_number: routingNumber,
      property_name: propertyName,
      temp_id: tempId,
      type: "image/jpeg",
    };
    return this.makePostRequest(url, data, true);
  }

  async findDuplicates(
    check_number: string,
    check_type: string,
    account_number: string,
    routing_number: string
  ): Promise<FindDuplicatesResponseType> {
    const url = "v1/check_scan/duplicates";
    const data: FindDuplicatesRequestType = {
      request_id: uuidv4(),
      check_number,
      check_type: check_type.toUpperCase(),
      account_number,
      routing_number,
    };
    return this.makePostRequest(url, data, true);
  }

  async submitCheck(request: SubmitCheckRequestType): Promise<SubmitCheckResponseType> {
    const url = "v1/check_scan/payment";
    return this.doWithTimeoutRetries(() => {
      return this.makePostRequest(url, request, true);
    });
  }

  async addPaymentSource(body: AddPaymentMethodBody, signal?: AbortSignal) {
    const {
      type,
      expDateYear,
      expDateMonth,
      paymentScheduleType = "ONE_SHOT",
      cardNumber,
      cvv,
      zip,
      streetAddress2,
      allowCof = true,
      paymentType = "RENT",
      reservationId,
      paypageRegistrationId,
      last4,
      bin,
      threatMetrixSessionId,
      ...requiredAttributes
    } = body;
    const url = `v1/my/payment_source`;
    const finalBody = {
      request_id: nanoid(36),
      cardholder: requiredAttributes.cardholder,
      payment_source_type: requiredAttributes.paymentSourceType,
      street_address: requiredAttributes.streetAddress,
      paypage_registration_id: paypageRegistrationId,
      last4,
      bin,
      vantiv_card_type: type,
      address_line2: streetAddress2 ? streetAddress2 : undefined,
      payment_schedule_type: paymentScheduleType,
      payment_type: paymentType,
      allow_cof: allowCof,
      state: requiredAttributes.state,
      city: requiredAttributes.city,
      cvv: cvv,
      exp_date: requiredAttributes.expDate,
      zip: `${zip}`,
      reservation_id: `${reservationId}`,
      card_number: cardNumber ? cardNumber + "" : null,
      threat_metrix_session_id: threatMetrixSessionId,
      nickname: requiredAttributes.nickname,
    };

    if (expDateMonth && expDateYear) {
      const expDate = getExpDate({ expDateMonth, expDateYear });
      finalBody.exp_date = expDate;
    }
    return this.makePostRequest(url, finalBody, true, true, signal);
  }

  async updatePaymentSource(body: any) {
    const { payment_source_id, ...rest } = body;
    const url = `v1/my/payment_source/update/${payment_source_id}`;
    const finalBody = { ...rest, request_id: nanoid(36) };
    this.logger.log({ finalBody, url });
    return this.makePatchRequest(url, finalBody, true);
  }

  async verifyBillingAddress(body: any) {
    const { payment_source_id, ...rest } = body;
    const url = `v1/my/payment_source/${payment_source_id}/verify_full_address`;
    const finalBody = { ...rest, request_id: nanoid(36) };
    this.logger.log({ finalBody, url });
    return this.makePostRequest(url, finalBody, true);
  }
  async createPayment(body: CreatePaymentBody) {
    const { reservationId, ...requiredAttributes } = body;
    const url = `v1/my/reservation/${reservationId}/payment`;
    const finalBody = requiredAttributes;
    this.logger.log({ finalBody, url });
    return this.makePostRequest(url, finalBody, true);
  }
  async getUserRoles() {
    const url = `v1/my/account/user_roles`;
    return this.makeGetRequest(url, true);
  }
  async getPaymentSources() {
    const url = `v1/my/payment_source`;
    return this.makeGetRequest(url, true);
  }

  async deletePaymentSource(payment_source_id: string | number) {
    const url = `v1/my/payment_source/delete/${payment_source_id}`;
    return this.makeDeleteRequest(url, true);
  }
  async getPaymentHistory(reservationId: string) {
    const url = `v1/my/reservation/${reservationId}/payment`;
    this.logger.log({ url });
    return this.makeGetRequest(url, true);
  }
  async getResident(propId: string, householdId: string, residentId: string) {
    const query = new URLSearchParams({
      householdId,
      includeLedger: "true",
      ...(residentId ? { residentId } : {}),
    });
    const url = `v1/my/property/${propId}/resident?${query}`;
    return this.makeGetRequest(url, true);
  }
  async getReservations() {
    const url = `v1/my/reservation`;
    return this.makeGetRequest(url, true);
  }
  async hideReservation(id: string) {
    const url = `v1/my/reservation/${id}/hide`;
    return this.makePutRequest(url, {}, true);
  }
  async makePayment({ reservationId, domusoApiService, cof, canccellationRefund }: any) {
    // TODO: fill me in
    this.logger.log(`Fill me in`);
  }
  async cancelAutoPay(reservationId: any) {
    const url = `v1/my/reservation/${reservationId}/cancelAutoPay`;
    this.logger.log("About to make put request");
    return this.makePutRequest(url, {}, true);
  }

  async getReservationBalance({
    id,
    accounting_id: household_id,
    prop_id: property_id,
    yardi_id: resident_id,
  }: any) {
    const query = new URLSearchParams({ household_id, property_id, resident_id });
    const url = `v1/my/reservation/${id}/balance_due?${query}`;
    return this.makeGetRequest(url, true);
  }
  async checkDuplicatePayment({
    amount,
    payment_type,
    payment_source_id,
    date_iso,
    reservationId,
    paymentSourceType,
  }: Record<string, any>) {
    const query = new URLSearchParams({
      amount,
      payment_type,
      date_iso,
      payment_source_id,
      paymentSourceType,
    });
    const url = `v1/my/reservation/${reservationId}/check_duplicate_payment?${query}`;
    return this.makeGetRequest(url, true);
  }
  async checkSimilarPayment({
    amount,
    payment_type,
    payment_source_id,
    date_iso,
    reservationId,
  }: Record<string, any>) {
    const query = new URLSearchParams({ amount, payment_type, date_iso, payment_source_id });
    const url = `v1/my/reservation/${reservationId}/check_similar_payment?${query}`;
    return this.makeGetRequest(url, true);
  }
  async checkUserCCPayBlock({ reservationId }: Record<string, any>) {
    const url = `v1/my/reservation/${reservationId}/check_cc_pay_block`;
    return this.makeGetRequest(url, true);
  }
  async getBannerMessage(propId: string) {
    const url = `v1/my/property/${propId}/banner_message`;
    return this.makeGetRequest(url, true);
  }
  async disableAccount() {
    const url = `v1/my/account/disable`;
    return this.makePutRequest(url, {}, true);
  }

  async getAppReviewData() {
    const url = `v1/app_review`;
    return this.makeGetRequest(url, true);
  }

  async submitAppReviewData(body: SubmitAppReviewDataBody) {
    const url = `v1/app_review`;
    return this.makePostRequest(url, body, true);
  }

  async getYodleeVersion() {
    const url = `v1/my/yodlee/version`;
    return this.makeGetRequest(url, true);
  }

  async getFeesByPropertyId(propertyId: string | number, amount: number) {
    const url = `v1/property/${propertyId}/feesForProperty`;
    const data = {
      amount,
    };
    return this.makePostRequest(url, data, true);
  }

  customizeHeaders(headers: any): any {
    const customizedHeaders = super.customizeHeaders(headers);
    if (Platform.OS == "web") {
      return { ...customizedHeaders };
    }
    const customHeader = { "x-app-env": config.env, version: Constants?.expoConfig?.version };
    return { ...customizedHeaders, ...customHeader };
  }

  async updatePersonalInfo(body: any) {
    const url = `v1/update_personal_info`;
    const finalBody = { ...body };
    return this.makePostRequest(url, finalBody, true);
  }

  async updatePersonalInfoEmailInitiate(body: any) {
    const url = `v1/update_personal_info_email_initiate`;
    const finalBody = { ...body };
    return this.makePostRequest(url, finalBody, true);
  }

  async updatePersonalInfoEmailComplete(body: any) {
    const url = `v1/update_personal_info_email_complete`;
    const finalBody = { ...body };
    return this.makePostRequest(url, finalBody, true);
  }

  async getAccountDetails() {
    const url = `v1/my/account`;
    return this.makeGetRequest(url, true);
  }
}
